import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import {useMutation} from "@tanstack/react-query";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {confirmDevice, requestDeviceChallenge} from "../../../api/deviceApi";
import {i18_TRANSLATION} from "../../../i18nReferences";
import {
    getCodeValidMinutes,
    getDeviceUnlockProblem,
    getDeviceUnlockProblemText,
    isCompleteCode,
    normalizeCode,
    PROBLEM_CODE_EXPIRED,
    RESEND_DELAY_MS,
    STEP_CHALLENGE,
    STEP_CONFIRM,
} from "./deviceUnlockModel";

/**
 * The dialog "unlock device": ask for the code (mailed to the login address), enter the 6 digits, confirm.
 * It is mounted only while it is shown, so every opening starts again at the first step.
 *
 * - `onCancel` is given where the user may decline (a write that needs the code); without it, the only way out
 *   besides the code is `onLogout` (a guest on an unknown device may not even read).
 * - `onConfirmed` is called after `confirmDevice` has stored the new access token.
 */
const DeviceUnlockDialog = ({onCancel, onLogout, onConfirmed}) => {
    const {t} = useTranslation([i18_TRANSLATION]);
    const [code, setCode] = useState("");
    // Counts the codes sent for this opening; 0 means none is valid (nothing sent yet, or it expired).
    const [sentCount, setSentCount] = useState(0);
    const [validMinutes, setValidMinutes] = useState(() => getCodeValidMinutes());
    const [canResend, setCanResend] = useState(false);

    const challengeMutation = useMutation({
        mutationFn: requestDeviceChallenge,
        onSuccess: ({data}) => {
            setValidMinutes(getCodeValidMinutes(data?.validForSeconds));
            setCanResend(false);
            setSentCount(count => count + 1);
        },
    });
    const confirmMutation = useMutation({
        mutationFn: ({code: enteredCode}) => confirmDevice({code: enteredCode}),
        onSuccess: onConfirmed,
        onError: error => {
            if (getDeviceUnlockProblem(error, STEP_CONFIRM) === PROBLEM_CODE_EXPIRED) {
                setCode("");
                setSentCount(0);
            }
        },
    });

    useEffect(() => {
        if (sentCount === 0) {
            return undefined;
        }
        const timer = setTimeout(() => setCanResend(true), RESEND_DELAY_MS);
        return () => clearTimeout(timer);
    }, [sentCount]);

    const isCodeSent = sentCount > 0;
    const isBusy = challengeMutation.isPending || confirmMutation.isPending;
    const failure = confirmMutation.isError
        ? {error: confirmMutation.error, step: STEP_CONFIRM}
        : challengeMutation.isError
          ? {error: challengeMutation.error, step: STEP_CHALLENGE}
          : null;

    const handleRequestCode = () => {
        confirmMutation.reset();
        challengeMutation.mutate();
    };
    const handleSubmit = event => {
        event.preventDefault();
        if (isCodeSent && isCompleteCode(code) && !isBusy) {
            challengeMutation.reset();
            confirmMutation.mutate({code: normalizeCode(code)});
        }
    };

    return (
        <Dialog open fullWidth maxWidth="xs" onClose={onCancel} data-testid="device-unlock-dialog">
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    {t("DEVICE_UNLOCK.TITLE", {ns: i18_TRANSLATION, defaultValue: "Gerät freischalten"})}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <DialogContentText>
                            {t("DEVICE_UNLOCK.INFO", {
                                ns: i18_TRANSLATION,
                                defaultValue:
                                    "Dieses Gerät ist noch nicht freigeschaltet. KissCal schickt einen Code per E-Mail an die Login-Adresse.",
                            })}
                        </DialogContentText>
                        {isCodeSent ? (
                            <>
                                <DialogContentText>
                                    {t("DEVICE_UNLOCK.SENT_INFO", {
                                        ns: i18_TRANSLATION,
                                        defaultValue: "Der Code wurde verschickt und ist {{minutes}} Minuten gültig.",
                                        minutes: validMinutes,
                                    })}
                                </DialogContentText>
                                <TextField
                                    name="code"
                                    autoFocus
                                    variant="standard"
                                    fullWidth
                                    value={code}
                                    onChange={event => setCode(event.target.value)}
                                    autoComplete="one-time-code"
                                    slotProps={{htmlInput: {inputMode: "numeric"}}}
                                    label={t("DEVICE_UNLOCK.CODE_LABEL", {
                                        ns: i18_TRANSLATION,
                                        defaultValue: "Code (6 Ziffern)",
                                    })}
                                />
                            </>
                        ) : null}
                        {failure ? (
                            <Alert severity="error" data-testid="device-unlock-error">
                                {getDeviceUnlockProblemText(getDeviceUnlockProblem(failure.error, failure.step), t)}
                            </Alert>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    {onCancel ? (
                        <Button onClick={onCancel} disabled={confirmMutation.isPending} data-testid="device-unlock-cancel">
                            {t("BUTTON.ABBRUCH", {ns: i18_TRANSLATION})}
                        </Button>
                    ) : (
                        <Button onClick={onLogout} disabled={confirmMutation.isPending} data-testid="device-unlock-logout">
                            {t("DEVICE_UNLOCK.LOGOUT_BUTTON", {ns: i18_TRANSLATION, defaultValue: "Abmelden"})}
                        </Button>
                    )}
                    {isCodeSent && canResend ? (
                        <Button onClick={handleRequestCode} disabled={isBusy} data-testid="device-unlock-resend">
                            {t("DEVICE_UNLOCK.REQUEST_BUTTON", {ns: i18_TRANSLATION, defaultValue: "Code anfordern"})}
                        </Button>
                    ) : null}
                    {isCodeSent ? (
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isBusy || !isCompleteCode(code)}
                            startIcon={confirmMutation.isPending ? <CircularProgress size={16} /> : null}
                            data-testid="device-unlock-submit"
                        >
                            {t("STUDIO_LOGIN.CONFIRM_BUTTON", {ns: i18_TRANSLATION})}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleRequestCode}
                            disabled={isBusy}
                            startIcon={challengeMutation.isPending ? <CircularProgress size={16} /> : null}
                            data-testid="device-unlock-request"
                        >
                            {t("DEVICE_UNLOCK.REQUEST_BUTTON", {ns: i18_TRANSLATION, defaultValue: "Code anfordern"})}
                        </Button>
                    )}
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default DeviceUnlockDialog;
